/** The SPA's read path onto `/catalog/search` — server-side Discover.
 *
 *  The marketplace SPA used to download BOTH catalogues whole on every
 *  hydration (1.62 MB raw / ~200 KB brotli of recipes, 1.28 MB / ~200 KB of
 *  packs) and search, filter, sort and page them in the browser, to render 48
 *  cards. This is the read that replaces it.
 *
 *  ⚠ THIS MODULE HOLDS NO SEARCH SEMANTICS, deliberately. What matches, how it
 *  ranks and how facets are counted is defined ONCE — in `discover-specs.ts`,
 *  reproduced in SQL by `discover_listings` (migrations 029/030), and proven
 *  equal by `scripts/verify-discover-parity.mjs` over two corpora. The SPA's
 *  search behaviour IS that spec now; it holds no filter or sort code of its
 *  own. Adding "just one" client-side narrowing here would fork it again.
 *
 *  Wire shape mirrors the webclient's `catalog-client.ts` — restated rather than
 *  imported because the SPA cannot reach into webclient code. The two are kept
 *  honest by the endpoint they both call, not by a shared type.
 */

import type { FacetCount, PackListing, RecipeListing } from './template.js';

export type CatalogKind = 'recipe' | 'pack';

/** The query the endpoint takes. `filters` keys are facet keys as declared in
 *  the spec (`platform` / `tag` / `publisher` / `service_kind` / `pack_kind`);
 *  an unknown key matches nothing rather than being ignored, which is how a
 *  stale client fails loudly instead of silently widening the set. */
export interface CatalogSearchQuery {
  search: string;
  filters: Record<string, readonly string[]>;
  sort: string;
  page: number;
  perPage: number;
}

/** One page of server-computed results. */
export interface CatalogPage<Row> {
  rows: Row[];
  total: number;
  totalPages: number;
  page: number;
  /** Drop-one-out counts, per facet key, already ordered count-desc /
   *  value-asc by the RPC. A facet's OWN selection is excluded from its own
   *  counts — that is what keeps a selected chip from reading "0". */
  facets: Record<string, FacetCount[]>;
}

/** ⚠ The wire shape the stage-3 differential proved against the live corpus —
 *  `f.<facet>=a,b`, comma-joined. The endpoint also accepts repeated `f.<facet>`
 *  params, but only this form has been shown equal to `runDiscover`
 *  end-to-end, so it is the form the client sends. */
export const catalogSearchParams = (
  kind: CatalogKind,
  query: CatalogSearchQuery,
): URLSearchParams => {
  const p = new URLSearchParams({
    kind,
    q: query.search,
    // Always explicit. An empty `sort` falls back to the spec's FIRST sorter
    // (`downloads`), which is not the SPA's default (`newest`) — leaving it off
    // would silently re-rank every unfiltered browse.
    sort: query.sort,
    page: String(query.page),
    per_page: String(query.perPage),
  });
  for (const [key, values] of Object.entries(query.filters)) {
    if (values.length > 0) p.set(`f.${key}`, values.join(','));
  }
  return p;
};

const isRecord = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v);

const facetsOf = (u: unknown): Record<string, FacetCount[]> => {
  if (!isRecord(u)) return {};
  const out: Record<string, FacetCount[]> = {};
  for (const [key, raw] of Object.entries(u)) {
    if (!Array.isArray(raw)) continue;
    const vals: FacetCount[] = [];
    for (const entry of raw) {
      if (!isRecord(entry)) continue;
      if (typeof entry.value !== 'string' || typeof entry.count !== 'number') continue;
      vals.push({ value: entry.value, count: entry.count });
    }
    out[key] = vals;
  }
  return out;
};

/** Ask the server for ONE page of results.
 *
 *  Throws on a network / HTTP / malformed-response failure, matching the other
 *  fetches in this app — the callers decide whether that blanks the view, keeps
 *  the server-rendered document, or just leaves the last page on screen.
 *
 *  `total` and `totalPages` are asserted rather than defaulted: they drive the
 *  pager and the "N of M" line, so a missing one is a malformed response, not a
 *  zero. Defaulting them would strand every reader on page 1. */
export const fetchCatalogSearch = async <Row>(
  kind: CatalogKind,
  query: CatalogSearchQuery,
  opts: { signal?: AbortSignal } = {},
): Promise<CatalogPage<Row>> => {
  const res = await fetch(`/catalog/search?${catalogSearchParams(kind, query)}`, {
    headers: { accept: 'application/json' },
    ...(opts.signal !== undefined ? { signal: opts.signal } : {}),
  });
  if (!res.ok) throw new Error(`Failed to search ${kind}s: ${res.status}`);
  const body = (await res.json()) as unknown;
  if (!isRecord(body) || typeof body.total !== 'number' || typeof body.totalPages !== 'number') {
    throw new Error(`Failed to search ${kind}s: malformed response`);
  }
  return {
    rows: (Array.isArray(body.rows) ? body.rows : []) as Row[],
    total: body.total,
    totalPages: Math.max(1, body.totalPages),
    page: typeof body.page === 'number' ? body.page : query.page,
    facets: facetsOf(body.facets),
  };
};

export const searchRecipes = (
  query: CatalogSearchQuery,
  opts?: { signal?: AbortSignal },
): Promise<CatalogPage<RecipeListing>> => fetchCatalogSearch('recipe', query, opts);

export const searchPacks = (
  query: CatalogSearchQuery,
  opts?: { signal?: AbortSignal },
): Promise<CatalogPage<PackListing>> => fetchCatalogSearch('pack', query, opts);
